import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import browserHistory from '../history';
import Header from './authentication/Header';

let squareCard     = null;
let cashAppPay     = null;
let squareApplePay = null;
let squareGooglePay = null;

function getUniqueCards(cards) {
  const map = new Map();
  (cards || []).forEach(c => {
    const key = `${c.exp_month}-${c.exp_year}-${c.last_4}-${c.bin}`;
    if (!map.has(key)) map.set(key, c);
  });
  return Array.from(map.values());
}

export default function SubscriptionPlans() {
  const siteSettings      = window.site || {};
  const siteConfig        = siteSettings.config || {};
  const appSettings       = window.app || {};
  const appStorage        = appSettings.storage || localStorage;
  const token             = appStorage.getItem('token') || localStorage.getItem('token');
  const appname           = appSettings.appName || '';
  const environmentVariable = appSettings.appEnvName || 'dev';
  const lambdaUrl         = siteSettings.screeningApiBaseUrl || siteSettings.submissionApiBaseUrl || '';

  const squareConfig  = (siteConfig.paymentGateway || {}).squareUp || {};
  const appId         = squareConfig.appId || '';
  const locationId    = squareConfig.locationId || '';
  const isSandbox     = appId.startsWith('sandbox');
  const currencySymbol = ((siteConfig.paymentGateway || {}).currency || {}).symbol || '$';

  const subscriptionPlans = (siteConfig.subscription || {}).subscriptionPlans?.plans || [];

  const storedFirst = appStorage.getItem('firstname') || '';
  const storedLast  = appStorage.getItem('lastname')  || '';
  const storedEmail = appStorage.getItem('email')     || '';

  const [selectedPlan, setSelectedPlan]   = useState(null);
  const [step, setStep]                   = useState('plans');
  const [fdBilling, setFdBilling]         = useState({
    firstName:      storedFirst,
    lastName:       storedLast,
    email:          storedEmail,
    cardholderName: `${storedFirst} ${storedLast}`.trim(),
    billingAddress: '',
    city:           '',
    zipCode:        '',
    country:        '',
    state:          '',
  });
  const [cards, setCards]                 = useState([]);
  const [selectedCard, setSelectedCard]   = useState('');
  const [processing, setProcessing]       = useState(false);
  const [paymentError, setPaymentError]   = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [squareReady, setSquareReady]     = useState(false);
  const [sdkLoadError, setSdkLoadError]   = useState(null);

  // Refs so event listeners (apple/google/cashapp) always read latest values
  const fdBillingRef    = useRef(fdBilling);
  const selectedPlanRef = useRef(selectedPlan);
  useEffect(() => { fdBillingRef.current = fdBilling; }, [fdBilling]);
  useEffect(() => { selectedPlanRef.current = selectedPlan; }, [selectedPlan]);

  const cardContainerRef  = useRef(null);
  const appleButtonRef    = useRef(null);
  const googleButtonRef   = useRef(null);
  const cashAppRef        = useRef(null);
  const initGuardRef      = useRef(false);

  useEffect(() => {
    if (step !== 'payment') return;
    loadCards();
    initSquare();
    return () => {
      // cleanup: destroy Square instances when leaving payment step
      if (squareCard)      { squareCard.destroy().catch(() => {}); squareCard = null; }
      if (cashAppPay)      { cashAppPay.destroy().catch(() => {}); cashAppPay = null; }
      if (squareApplePay)  { squareApplePay.destroy().catch(() => {}); squareApplePay = null; }
      if (squareGooglePay) { squareGooglePay.destroy().catch(() => {}); squareGooglePay = null; }
      initGuardRef.current = false;
    };
  }, [step]);

  /* ── Cards list ────────────────────────────────────────── */
  const loadCards = () => {
    if (!lambdaUrl || !token) return;
    axios.post(`${lambdaUrl}/cardslist?appname=${appname}&env=${environmentVariable}&token=${token}`)
      .then(response => {
        if (response?.data?.statusCode === 200) {
          const saved = response?.data?.result?.[0]?.savedCards;
          if (saved?.length > 0) setCards(getUniqueCards(saved));
        }
      })
      .catch(() => {});
  };

  /* ── Square SDK loader ──────────────────────────────────── */
  const loadSquareSDK = () => new Promise((resolve, reject) => {
    if (window.Square) return resolve();
    const script = document.createElement('script');
    script.src = isSandbox
      ? 'https://sandbox.web.squarecdn.com/v1/square.js'
      : 'https://web.squarecdn.com/v1/square.js';
    script.onload  = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Square SDK'));
    document.head.appendChild(script);
  });

  /* ── Payment request helper ─────────────────────────────── */
  const buildPaymentRequest = (paymentsObj) => paymentsObj.paymentRequest({
    countryCode: 'US',
    currencyCode: 'USD',
    total: {
      amount: String(selectedPlanRef.current?.price || '0'),
      label:  'Total',
    },
  });

  /* ── Initialize card ────────────────────────────────────── */
  const initializeCard = async (paymentsObj) => {
    try {
      const cardSource = await paymentsObj.card({
        style: {
          'input':             { color: '#ffffff', fontSize: '15px' },
          'input::placeholder':{ color: 'rgba(255,255,255,0.4)' },
          '.message-text':     { color: '#ffffff' },
          '.message-icon':     { color: '#ffffff' },
        },
      });
      if (squareCard) { try { await squareCard.destroy(); } catch (e) {} squareCard = null; }
      await cardSource.attach('#card-container');
      squareCard = cardSource;
      setSquareReady(true);
    } catch (err) {
      console.error('initializeCard error', err);
    }
  };

  /* ── Initialize Apple Pay ───────────────────────────────── */
  const initializeApplePay = async (paymentsObj) => {
    try {
      const paymentRequest = buildPaymentRequest(paymentsObj);
      const plan     = selectedPlanRef.current;
      const planId   = plan?.squarePlanId || plan?.planId || '';
      const planName = plan?.planName || '';
      const email    = appStorage.getItem('email') || '';
      const refId    = `${appname}_${planId}_${planName}_${email}`;

      const applePaySource = await paymentsObj.applePay(paymentRequest, {
        redirectURL: window.location.href,
        referenceId: refId,
      });
      squareApplePay = applePaySource;

      const btn = document.getElementById('square-apple-pay-button');
      if (btn) {
        btn.addEventListener('click', async () => {
          try {
            const tokenResult = await applePaySource.tokenize();
            if (tokenResult.status === 'OK') {
              await paymentSquare(tokenResult.token, {});
            } else {
              setPaymentError(`Apple Pay failed: ${tokenResult.status}`);
            }
          } catch (err) {
            console.error('ApplePay tokenization error', err);
          }
        });
      }
    } catch (err) {
      console.warn('initializeApplePay error', err.message);
    }
  };

  /* ── Initialize Google Pay ──────────────────────────────── */
  const initializeGooglePay = async (paymentsObj) => {
    try {
      const googleContainer = document.getElementById('square-google-pay-button');
      if (googleContainer) googleContainer.innerHTML = '';

      const paymentRequest = buildPaymentRequest(paymentsObj);
      const googlePay = await paymentsObj.googlePay(paymentRequest);
      squareGooglePay = googlePay;
      await googlePay.attach('#square-google-pay-button', {
        buttonColor:    'black',
        buttonSizeMode: 'fill',
        buttonType:     'long',
      });

      const btn = document.getElementById('square-google-pay-button');
      if (btn) {
        btn.addEventListener('click', async () => {
          try {
            const tokenResult = await googlePay.tokenize();
            if (tokenResult.status === 'OK') {
              await paymentSquare(tokenResult.token, {});
            } else {
              setPaymentError(`Google Pay failed: ${tokenResult.status}`);
            }
          } catch (err) {
            console.error('GooglePay tokenization error', err);
          }
        });
      }
    } catch (err) {
      console.warn('initializeGooglePay error', err.message);
    }
  };

  /* ── Initialize Cash App Pay ────────────────────────────── */
  const initializeCashApp = async (paymentsObj) => {
    try {
      const paymentRequest = buildPaymentRequest(paymentsObj);
      const plan     = selectedPlanRef.current;
      const planId   = plan?.squarePlanId || plan?.planId || '';
      const planName = plan?.planName || '';
      const email    = appStorage.getItem('email') || '';
      const refId    = `${appname}_${planId}_${planName}_${email}`;

      const cashAppPaySource = await paymentsObj.cashAppPay(paymentRequest, {
        redirectURL: window.location.href,
        referenceId: refId,
      });
      cashAppPay = cashAppPaySource;
      await cashAppPaySource.attach('#cash-app-pay', { shape: 'semiround', width: 'full' });

      cashAppPaySource.addEventListener('ontokenization', async (event) => {
        try {
          const { tokenResult } = event.detail;
          if (tokenResult.status === 'OK') {
            await paymentSquare(tokenResult.token, {});
          } else {
            setPaymentError(`Cash App Pay failed: ${tokenResult.status}`);
          }
        } catch (err) {
          console.error('cashApp ontokenization error', err);
        }
      });
    } catch (err) {
      console.warn('initializeCashApp error', err.message);
    }
  };

  /* ── Main Square init ───────────────────────────────────── */
  const initSquare = async () => {
    if (initGuardRef.current || !appId || !locationId) return;
    initGuardRef.current = true;
    try {
      await loadSquareSDK();
      setSdkLoadError(null);
      let paymentsObj;
      try {
        paymentsObj = window.Square.payments(appId, locationId);

      } catch (err) {
        console.warn('Square.payments() init failed:', err.message);
        setSdkLoadError('Unable to connect to payment processor.');
        return;
      }
      await Promise.all([
        initializeCard(paymentsObj).catch(err => {
          console.warn('Card init failed:', err.message);
          setSdkLoadError('Unable to load card payment form.');
          return null;
        }),
        initializeCashApp(paymentsObj).catch(err => {
          console.warn('CashApp init failed:', err.message);
          return null;
        }),
        initializeApplePay(paymentsObj).catch(() => null),
        initializeGooglePay(paymentsObj).catch(() => null),
      ]);
    } catch (err) {
      console.error('Square init error', err);
      setPaymentError('Payment form failed to load. Please refresh and try again.');
    }
  };

  /* ── Plan selection ─────────────────────────────────────── */
  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setStep('payment');
    setPaymentError('');
    setPaymentSuccess(false);
    setSquareReady(false);
    setSdkLoadError(null);
    setSelectedCard('');
    setCards([]);
    squareCard = null; cashAppPay = null; squareApplePay = null; squareGooglePay = null;
  };

  const billField = (field) => (e) =>
    setFdBilling(prev => ({ ...prev, [field]: e.target.value }));

  /* ── Core payment function (mirrors PaymentAuth.jsx) ────── */
  const paymentSquare = async (paymentToken, cardPayload) => {
    setProcessing(true);
    setPaymentError('');

    const billing      = fdBillingRef.current;
    const plan         = selectedPlanRef.current;
    const authToken    = appStorage.getItem('token') || localStorage.getItem('token');
    const cardholderName = `${billing.firstName || ''} ${billing.lastName || ''}`.trim() || billing.cardholderName;
    const price        = plan.price;
    const planId       = plan.squarePlanId || plan.planId || plan._id || '';
    const planName     = plan.planName || '';

    let dataparams = 'payment_method_nonce=' + paymentToken
      + '&token='           + authToken
      + '&cardholderName='  + encodeURIComponent(cardholderName)
      + '&price='           + price
      + '&transactionType=subscription'
      + '&itemType=subscription'
      + '&userassetType=subscription'
      + '&planId='          + encodeURIComponent(planId)
      + '&planName='        + encodeURIComponent(planName);

    if (locationId) {
      dataparams += '&paymentCurrency=USD&gatewayType=cashapp&sourceId=' + paymentToken + '&locationId=' + locationId;
    }
    if (cardPayload?.cardBrand) dataparams += '&cardBrand=' + cardPayload.cardBrand;
    if (cardPayload?.last4)     dataparams += '&last4='     + cardPayload.last4;
    if (cardPayload?.expMonth)  dataparams += '&expMonth='  + cardPayload.expMonth;
    if (cardPayload?.expYear)   dataparams += '&expYear='   + cardPayload.expYear;

    const billinginfo = {
      firstName:      billing.firstName      || '',
      lastName:       billing.lastName       || '',
      email:          billing.email          || '',
      billingAddress: billing.billingAddress || '',
      city:           billing.city           || '',
      zipCode:        billing.zipCode        || '',
      country:        billing.country        || '',
      state:          billing.state          || '',
    };

    const senderInfo = {
      senderName:  cardholderName,
      senderEmail: billing.email || '',
    };

    const assetObj = {
      assetid:       planId,
      assetName:     planName,
      description:   planName,
      single_price:  price,
      userassetType: 'subscription',
      senderInfo,
      billinginfo,
    };

    try {
      const response = await axios.post(
        `${lambdaUrl}/paymentAuth?appname=${appname}&env=${environmentVariable}&${dataparams}`,
        [assetObj]
      );
      if (response?.data?.statusCode === 200) {
        setPaymentSuccess(true);
      } else {
        setPaymentError('Payment failed. Please try again.');
      }
    } catch (err) {
      console.error('paymentSquare error', err);
      setPaymentError('Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  /* ── Card tokenize → paymentSquare ─────────────────────── */
  const cardPayment = async () => {
    if (!squareCard) { setPaymentError('Payment form not ready.'); return; }
    try {
      const tokenResult = await squareCard.tokenize();
      if (tokenResult?.status === 'OK') {
        const cardPayload = {
          sourceId:  tokenResult.token,
          cardBrand: tokenResult.details?.card?.brand,
          last4:     tokenResult.details?.card?.last4,
          expMonth:  tokenResult.details?.card?.expMonth,
          expYear:   tokenResult.details?.card?.expYear,
        };
        console.debug('Card tokenization successful', { tokenResult, cardPayload });
        await paymentSquare(tokenResult.token, cardPayload);
      } else {
        setPaymentError(tokenResult?.errors?.map(e => e.message).join(', ') || 'Tokenization failed.');
      }
    } catch (err) {
      console.error('cardPayment error', err);
      setPaymentError('Payment failed. Please try again.');
    }
  };

  /* ── Pay with saved card ────────────────────────────────── */
  const payWithCard = async (cardId) => {
    await paymentSquare(cardId, {});
  };

  const getPeriodLabel = (plan) => {
    if (plan.period == '30') return '/mo';
    if (plan.period == '365' || plan.period == '12') return '/yr';
    return '';
  };

  /* ── Styles ─────────────────────────────────────────────── */
  const S = {
    page:            { backgroundColor: 'var(--bgColor)', minHeight: '100vh', paddingTop: '53px' },
    container:       { maxWidth: '1300px', margin: '0 auto', padding: '40px 24px' },
    heading:         { color: '#ffffff', fontSize: '32px', fontWeight: 700, textAlign: 'center', marginBottom: '8px' },
    subheading:      { color: '#8892a4', fontSize: '16px', textAlign: 'center', marginBottom: '48px' },
    plansGrid:       { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 350px))', gap: '24px', justifyContent: 'center' },
    planCard:        { background: '#12152b', borderRadius: '16px', padding: '32px 28px 28px', display: 'flex', flexDirection: 'column', gap: '16px', border: '1px solid rgba(167,139,250,0.2)', position: 'relative' },
    planCardPopular: { border: '1px solid rgba(43,68,255,0.6)', background: 'linear-gradient(160deg, #13163a 0%, #12152b 60%)' },
    planBadge:       { position: 'absolute', top: '-13px', left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(90deg,#2B44FF,#7c3aed)', borderRadius: '20px', padding: '4px 16px', fontSize: '11px', fontWeight: 700, color: '#fff', letterSpacing: '1.5px', textTransform: 'uppercase', whiteSpace: 'nowrap' },
    planName:        { color: '#a78bfa', fontSize: '24px', fontWeight: 700, lineHeight: 1.2 },
    planPriceRow:    { display: 'flex', alignItems: 'baseline', gap: '6px' },
    planPriceAmt:    { color: '#fff', fontSize: '42px', fontWeight: 800, lineHeight: 1 },
    planPricePer:    { color: 'rgba(255,255,255,0.5)', fontSize: '14px', fontWeight: 400 },
    planDivider:     { border: 'none', borderTop: '1px solid rgba(255,255,255,0.07)', margin: '0' },
    planDesc:        { color: 'rgba(255,255,255,0.8)', fontSize: '14px', lineHeight: '1.6', flex: 1 },
    planDescItem:    { display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '4px' },
    planDescBullet:  { color: '#a78bfa', fontSize: '14px', flexShrink: 0, marginTop: '1px' },
    featuresGrid:    { display: 'none' },
    featureCol:      { display: 'flex', flexDirection: 'column', gap: '6px' },
    featureItem:     { display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '6px 0' },
    checkCircle:     { width: '26px', height: '26px', borderRadius: '50%', background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' },
    featureLabel:    { color: '#ffffff', fontSize: '14px', fontWeight: 500, lineHeight: '1.4' },
    subList:         { listStyle: 'none', padding: '6px 0 0 8px', margin: 0 },
    subItem:         { color: 'rgba(255,255,255,0.6)', fontSize: '13px', padding: '3px 0', display: 'flex', alignItems: 'center', gap: '8px' },
    subBullet:       { color: 'rgba(255,255,255,0.4)', fontSize: '16px', lineHeight: 1, flexShrink: 0 },
    selectBtn:       { width: '100%', background: 'linear-gradient(90deg,#2B44FF,#7c3aed)', border: 'none', borderRadius: '10px', padding: '14px 16px', color: '#ffffff', fontSize: '14px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', transition: 'opacity 0.2s', marginTop: 'auto' },
    payLayout:       { display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: '32px', maxWidth: '1100px', margin: '0 auto' },
    summaryCard:     { background: '#12152b', borderRadius: '16px', padding: '32px 28px', height: 'fit-content', position: 'sticky', top: '80px' },
    summaryHeading:  { color: '#8892a4', fontSize: '11px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '20px' },
    summaryPlanRow:  { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' },
    summaryPlanName: { color: '#ffffff', fontSize: '17px', fontWeight: 700 },
    summaryPlanPrice:{ color: '#a78bfa', fontSize: '22px', fontWeight: 800 },
    changeLink:      { background: 'none', border: 'none', color: '#a78bfa', fontSize: '13px', cursor: 'pointer', padding: 0, textDecoration: 'underline' },
    summaryDivider:  { borderColor: 'rgba(255,255,255,0.08)', margin: '16px 0' },
    summaryRow:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', color: '#8892a4', fontSize: '14px' },
    summaryTotal:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0 0', color: '#ffffff', fontSize: '16px', fontWeight: 700 },
    summaryTotalAmt: { color: '#a78bfa', fontSize: '20px', fontWeight: 800 },
    payCard:         { background: '#12152b', borderRadius: '16px', padding: '32px 28px' },
    payHeader:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
    payTitle:        { color: '#ffffff', fontSize: '16px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase' },
    cardBrands:      { display: 'flex', gap: '6px' },
    cardBrandBadge:  { background: 'rgba(255,255,255,0.08)', borderRadius: '4px', padding: '3px 7px', color: 'rgba(255,255,255,0.6)', fontSize: '11px', fontWeight: 600 },
    formGrid2:       { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
    formGroup:       { marginBottom: '14px' },
    label:           { display: 'block', color: '#ffffff', fontSize: '12px', marginBottom: '5px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' },
    inputField:      { width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid #2a2f4a', borderRadius: '6px', outline: 'none', color: '#ffffff', fontSize: '14px', padding: '10px 12px', boxSizing: 'border-box' },
    squareBox:       { borderRadius: '6px', padding: '2px 0', marginTop: '5px', minHeight: '44px', background: 'transparent', overflow: 'hidden' },
    orDivider:       { display: 'flex', alignItems: 'center', gap: '12px', margin: '20px 0' },
    orLine:          { flex: 1, borderTop: '1px solid rgba(255,255,255,0.1)' },
    orText:          { color: '#8892a4', fontSize: '12px', fontWeight: 500, letterSpacing: '1px' },
    savedCardsTitle: { color: '#8892a4', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' },
    savedCardRow:    { display: 'flex', alignItems: 'center', gap: '12px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '10px 14px', marginBottom: '8px', cursor: 'pointer', transition: 'border-color 0.15s' },
    savedCardRowActive: { borderColor: '#a78bfa', background: 'rgba(167,139,250,0.08)' },
    savedCardBrand:  { color: 'rgba(255,255,255,0.5)', fontSize: '10px', fontWeight: 700, background: 'rgba(255,255,255,0.08)', borderRadius: '3px', padding: '2px 6px', textTransform: 'uppercase' },
    savedCardNum:    { color: '#ffffff', fontSize: '14px', fontWeight: 600, flex: 1 },
    savedCardExp:    { color: '#8892a4', fontSize: '13px' },
    payWithCardBtn:  { background: '#2B44FF', color: '#fff', border: 'none', borderRadius: '40px', padding: '9px 20px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', letterSpacing: '0.5px' },
    payBtn:          { background: '#2B44FF', color: '#fff', border: 'none', borderRadius: '40px', padding: '14px', width: '100%', cursor: 'pointer', fontWeight: 700, fontSize: '15px', letterSpacing: '1px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '12px' },
    spinner:         { display: 'inline-block', width: '16px', height: '16px', border: '2px solid rgba(255,255,255,.3)', borderRadius: '50%', borderTopColor: '#fff', animation: 'spin 1s linear infinite' },
    errBox:          { background: 'rgba(192,57,43,0.15)', border: '1px solid #c0392b', borderRadius: '6px', color: '#e74c3c', fontSize: '13px', padding: '10px 14px', marginBottom: '16px', marginTop: '8px' },
    warnBox:         { background: 'rgba(230,168,0,0.1)', border: '1px solid rgba(230,168,0,0.4)', borderRadius: '6px', color: '#e6a800', fontSize: '12px', padding: '10px 14px', marginBottom: '12px' },
    successBox:      { background: 'rgba(26,127,75,0.15)', border: '1px solid #1a7f4b', borderRadius: '12px', padding: '40px 28px', textAlign: 'center', maxWidth: '480px', margin: '40px auto' },
    successTitle:    { color: '#ffffff', fontSize: '22px', fontWeight: 700, marginBottom: '8px' },
    successText:     { color: '#8892a4', fontSize: '15px' },
    backBtn:         { background: 'transparent', border: 'none', color: '#8892a4', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '14px', marginBottom: '28px', padding: 0 },
    sectionLabel:    { color: '#8892a4', fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '8px' },
  };

  const plans = Array.isArray(subscriptionPlans) ? subscriptionPlans : [];

  const subtotal = selectedPlan ? (parseFloat(selectedPlan.price) || 0) : 0;
  const tax      = 0;
  const total    = subtotal + tax;
  const fmtAmt   = (n) => `${currencySymbol}${n.toFixed(2)}`;

  return (
    <div style={S.page}>
      <Header />
      <div style={S.container}>

        {/* ── PLANS STEP ─────────────────────────────────── */}
        {step === 'plans' && (
          <>
            <h1 style={S.heading}>Choose Your Plan</h1>
            <p style={S.subheading}>Select the plan that works best for you</p>
            {plans.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#8892a4', padding: '60px 0' }}>
                No subscription plans available.
              </div>
            ) : (
              <div style={S.plansGrid} className="sp-plans-grid">
                {plans.map((plan, i) => {
                  const isFree     = plan.planId === 'free' || plan.price === 0 || plan.price === '0';
                  const isPopular  = !isFree && (plan.period == '365' || plan.period == '12' || plan.planName?.toLowerCase().includes('year'));
                  const priceAmt   = isFree ? 'Free' : `${currencySymbol}${plan.price}`;
                  const pricePer   = plan.priceLabel || (getPeriodLabel(plan) ? 'per ' + (plan.period == '30' ? 'month' : 'year') : '');
                  const cardStyle  = { ...S.planCard, ...(isPopular ? S.planCardPopular : {}) };
                  return (
                    <div key={i} style={cardStyle} className="sp-plan-card">
                      {isPopular && <div style={S.planBadge}>Best Value</div>}
                      <div style={S.planName} className="sp-plan-name">{plan.planName}</div>
                      <div style={S.planPriceRow}>
                        <span style={S.planPriceAmt}>{priceAmt}</span>
                        {pricePer && <span style={S.planPricePer}>{pricePer}</span>}
                      </div>
                      <hr style={S.planDivider} />
                      {plan.description && (
                        Array.isArray(plan.description) ? (
                          <div style={S.planDesc}>
                            {plan.description.map((d, di) => {
                              const text = typeof d === 'string' ? d : d.label || d.name || d.text || '';
                              return text ? (
                                <div key={di} style={S.planDescItem}>
                                  <span style={S.planDescBullet}>•</span>
                                  <span>{text}</span>
                                </div>
                              ) : null;
                            })}
                          </div>
                        ) : (
                          <div style={S.planDesc}>{plan.description}</div>
                        )
                      )}
                      <button
                        style={S.selectBtn}
                        disabled={!!plan.buttonDisable}
                        onClick={() => !plan.buttonDisable && handleSelectPlan(plan)}
                        onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                      >
                        SELECT PLAN
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ── PAYMENT STEP ───────────────────────────────── */}
        {step === 'payment' && selectedPlan && (
          <>
            <button style={S.backBtn} onClick={() => {
              setStep('plans'); setSquareReady(false); setPaymentError('');
              setCards([]); setSdkLoadError(null);
              squareCard = null; cashAppPay = null; squareApplePay = null; squareGooglePay = null;
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>arrow_back</span>
              Back to Plans
            </button>

            {paymentSuccess ? (
              <div style={S.successBox}>
                <span className="material-symbols-outlined" style={{ fontSize: '56px', color: '#1a7f4b', display: 'block', marginBottom: '12px' }}>check_circle</span>
                <div style={S.successTitle}>Payment Successful!</div>
                <p style={S.successText}>You're now subscribed to <strong>{selectedPlan.planName}</strong>. Enjoy your access!</p>
                <button style={{ ...S.payBtn, marginTop: '24px' }} onClick={() => { browserHistory.push(`/?appname=${appname}`); }}>
                  Go to Home
                </button>
              </div>
            ) : (
              <div style={S.payLayout} className="sp-pay-layout">

                {/* LEFT — ORDER SUMMARY */}
                <div style={S.summaryCard} className="sp-summary-card">
                  <div style={S.summaryHeading}>Order Summary</div>
                  <div style={S.summaryPlanRow}>
                    <div>
                      <div style={S.summaryPlanName}>{selectedPlan.planName}</div>
                      <div style={{ color: '#8892a4', fontSize: '13px', marginTop: '2px' }}>
                        {selectedPlan.priceLabel || (getPeriodLabel(selectedPlan) ? (selectedPlan.period == '30' ? 'Monthly' : 'Yearly') + ' plan' : '')}
                      </div>
                    </div>
                    <button style={S.changeLink} onClick={() => { setStep('plans'); setCards([]); }}>Change</button>
                  </div>
                  <div style={{ ...S.summaryPlanPrice, marginTop: '8px' }}>
                    {currencySymbol}{selectedPlan.price}{getPeriodLabel(selectedPlan)}
                  </div>
                  <hr style={S.summaryDivider} />
                  <div style={S.summaryRow}><span>Subtotal</span><span style={{ color: '#ffffff' }}>{fmtAmt(subtotal)}</span></div>
                  <div style={S.summaryRow}><span>Tax</span><span style={{ color: '#ffffff' }}>{fmtAmt(tax)}</span></div>
                  <hr style={S.summaryDivider} />
                  <div style={S.summaryTotal}>
                    <span>Total</span>
                    <span style={S.summaryTotalAmt}>{fmtAmt(total)}</span>
                  </div>
                </div>

                {/* RIGHT — PAYMENT FORM */}
                <div style={S.payCard}>
                  <div style={S.payHeader}>
                    <div style={S.payTitle}>Payment</div>
                    <div style={S.cardBrands}>
                      <span style={S.cardBrandBadge}>Visa</span>
                      <span style={S.cardBrandBadge}>Amex</span>
                      <span style={S.cardBrandBadge}>MC</span>
                    </div>
                  </div>

                  {/* Billing form */}
                  <div style={{ ...S.formGrid2, marginBottom: '14px' }} className="sp-form-grid2">
                    <div>
                      <label style={S.label}>First Name</label>
                      <input style={S.inputField} type="text" value={fdBilling.firstName} onChange={billField('firstName')} placeholder="First name" />
                    </div>
                    <div>
                      <label style={S.label}>Last Name</label>
                      <input style={S.inputField} type="text" value={fdBilling.lastName} onChange={billField('lastName')} placeholder="Last name" />
                    </div>
                  </div>
                  <div style={S.formGroup}>
                    <label style={S.label}>Email</label>
                    <input style={S.inputField} type="email" value={fdBilling.email} onChange={billField('email')} placeholder="Email address" />
                  </div>
                  <div style={S.formGroup}>
                    <label style={S.label}>Name on Card</label>
                    <input style={S.inputField} type="text" value={fdBilling.cardholderName} onChange={billField('cardholderName')} placeholder="Name on card" />
                  </div>
                  <div style={S.formGroup}>
                    <label style={S.label}>Billing Address</label>
                    <input style={S.inputField} type="text" value={fdBilling.billingAddress} onChange={billField('billingAddress')} placeholder="Street address" />
                  </div>
                  <div style={{ ...S.formGrid2, marginBottom: '14px' }} className="sp-form-grid2">
                    <div>
                      <label style={S.label}>City</label>
                      <input style={S.inputField} type="text" value={fdBilling.city} onChange={billField('city')} placeholder="City" />
                    </div>
                    <div>
                      <label style={S.label}>Postal Code</label>
                      <input style={S.inputField} type="text" value={fdBilling.zipCode} onChange={billField('zipCode')} placeholder="Postal code" />
                    </div>
                  </div>
                  <div style={{ ...S.formGrid2, marginBottom: '20px' }} className="sp-form-grid2">
                    <div>
                      <label style={S.label}>Country</label>
                      <input style={S.inputField} type="text" value={fdBilling.country} onChange={billField('country')} placeholder="Country" />
                    </div>
                    <div>
                      <label style={S.label}>State / Province</label>
                      <input style={S.inputField} type="text" value={fdBilling.state} onChange={billField('state')} placeholder="State / Province" />
                    </div>
                  </div>

                  {/* SDK load error */}
                  {sdkLoadError && (
                    <div style={S.warnBox}>
                      <span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'middle', marginRight: '6px' }}>warning</span>
                      {sdkLoadError}
                    </div>
                  )}

                  {/* All Square payment methods — mirrors PaymentAuth.jsx structure */}
                  <div id="cashpay-container" className="paymentopt cc_det">

                    {/* Cash App Pay */}
                    <div id="cashAppPay">
                      <div id="cash-app-pay" ref={cashAppRef} />
                    </div>

                    {/* Apple Pay */}
                    <div id="squareAppApplepay">
                      <div id="square-apple-pay-button" className="applepaySquare" ref={appleButtonRef} />
                    </div>

                    {/* Google Pay */}
                    <div id="squareAppGooglepay">
                      <div id="square-google-pay-button" ref={googleButtonRef} />
                    </div>

                    {/* Card field */}
                    <div id="cardContainer">
                      <label style={{ ...S.label, marginTop: '4px' }}>Card Details</label>
                      <div id="card-container" style={S.squareBox} ref={cardContainerRef}>
                        {!squareReady && !sdkLoadError && (
                          <div style={{ color: '#6f7685', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0' }}>
                            <span style={S.spinner} />
                            Loading payment form...
                          </div>
                        )}
                      </div>
                    </div>

                    {paymentError && <div style={S.errBox}>{paymentError}</div>}

                    {(!appId || !locationId) && (
                      <div style={S.errBox}>Payment gateway is not configured. Please contact support.</div>
                    )}

                    {/* Pay button for new card */}
                    {!selectedCard && (
                      <button
                        id="card-button"
                        type="button"
                        style={{ ...S.payBtn, opacity: processing || !squareReady ? 0.7 : 1 }}
                        onClick={cardPayment}
                        disabled={processing || !squareReady || !appId || !locationId}
                      >
                        {processing && <span style={S.spinner} />}
                        {processing ? 'Processing...' : `Pay ${fmtAmt(total)}`}
                      </button>
                    )}
                  </div>

                  {/* Saved cards — mirrors PaymentAuth.jsx */}
                  {cards.length > 0 && (
                    <>
                      <div style={S.orDivider}>
                        <div style={S.orLine} />
                        <span style={S.orText}>OR USE SAVED CARD</span>
                        <div style={S.orLine} />
                      </div>
                      <div style={S.savedCardsTitle}>Saved Cards</div>
                      <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                        {cards.map(c => {
                          const isActive = selectedCard === c.id;
                          return (
                            <div
                              key={c.id}
                              style={{ ...S.savedCardRow, ...(isActive ? S.savedCardRowActive : {}) }}
                              onClick={() => setSelectedCard(isActive ? '' : c.id)}
                            >
                              <input
                                type="radio"
                                name="savedCard"
                                value={c.id}
                                checked={isActive}
                                onChange={() => setSelectedCard(c.id)}
                                style={{ accentColor: '#a78bfa', cursor: 'pointer' }}
                              />
                              <span style={S.savedCardBrand}>{c.card_brand || 'CARD'}</span>
                              <span style={S.savedCardNum}>**** **** **** {c.last_4}</span>
                              <span style={S.savedCardExp}>{c.exp_month}/{c.exp_year}</span>
                              {isActive && (
                                <button
                                  style={S.payWithCardBtn}
                                  onClick={(e) => { e.stopPropagation(); payWithCard(selectedCard); }}
                                  disabled={processing}
                                >
                                  {processing ? 'Processing...' : 'Pay'}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}

                  <p style={{ color: '#6f7685', fontSize: '12px', textAlign: 'center', marginTop: '16px' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '14px', verticalAlign: 'middle', marginRight: '4px' }}>lock</span>
                    Secured by Square. Your card info is never stored on our servers.
                  </p>
                </div>

              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
}
